import { Monitor, Moon, Sun } from "lucide-react";
import type { JSX } from "react";

import { useTheme, type ThemeChoice } from "@/components/theme";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const OPTIONS: { value: ThemeChoice; label: string; Icon: typeof Sun }[] = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor }
];

/** Header control: pick light, dark, or follow the system. */
export const ThemeMenu = (): JSX.Element => {
  const { choice, resolved, setChoice } = useTheme();
  // The trigger shows what's actually on screen, so "System" reads as a sun or
  // moon rather than a monitor that doesn't say which way it resolved.
  const TriggerIcon = resolved === "dark" ? Moon : Sun;

  return (
    <Popover>
      <PopoverTrigger
        aria-label="Theme"
        className="inline-flex size-8 cursor-pointer items-center justify-center rounded-full border border-border bg-background/40 transition-colors hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
      >
        <TriggerIcon className="size-4" />
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-40 p-1">
        {OPTIONS.map(({ value, label, Icon }, idx) => (
          <button
            key={value}
            type="button"
            onClick={() => setChoice(value)}
            aria-pressed={choice === value}
            className={cn(
              "flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm outline-none",
              // Selected is shown by the filled background, not a check.
              choice === value
                ? "bg-accent font-semibold text-accent-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
              idx !== 0 && "mt-0.5"
            )}
          >
            <Icon className="size-4" />
            <span className="flex-1 text-left">{label}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
